import { FC, useEffect } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  HStack,
  Heading,
  Input,
  Radio,
  RadioGroup,
  Select,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';

import {
  useGetAllActionsQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useGetAllRolesQuery,
  useGetRoleByIdQuery,
  displayNotification,
} from '../../../services';

import { RoleForm } from '../models';
import { CenteredSpinner } from '../../../components';
import { useTypedSelector } from '../../../hooks';
import { PermissionList } from '../components';
import { CompanyStatus } from '../../../models';
import {
  isMutationSuccess,
  notifyMutationFailure,
} from '../../../utils/mutation-feedback';

export const SavedRole: FC = () => {
  const navigate = useNavigate();

  const params = useParams();
  const { roleId } = params;
  const status = params.status || 'all';

  const { userDetails } = useTypedSelector(state => state.auth);

  const { data: allRoles } = useGetAllRolesQuery(
    {
      page: 0,
      size: 10000,
      basicInfoId: Number(userDetails?.companyBasicInfo.id),
    },
    { skip: !userDetails }
  );

  const { isLoading: isAllActionsLoading } = useGetAllActionsQuery();

  const { data: savedRole } = useGetRoleByIdQuery(Number(roleId), {
    skip: !roleId,
  });

  const [createRole] = useCreateRoleMutation();
  const [updateRole] = useUpdateRoleMutation();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    control,
    reset,
    setValue,
    watch,
  } = useForm<RoleForm>();

  const navigateToPreviousPage = () => navigate(`/roles/${status}/`);

  async function onSubmit(data: RoleForm) {
    try {
      data.permission = data.permission.map(
        ({ permissionActions, permissionName }) => ({
          permissionName,
          permissionActions: permissionActions.map(({ action, enabled }) => ({
            action,
            enabled: enabled === 'true' || enabled,
          })),
        })
      );

      const reportTo = data.reportTo ? { id: data.reportTo } : null;

      if (roleId) {
        // Neither trigger is unwrapped: a rejected save resolved normally, so
        // the page toasted "Successfully Updated" and navigated away.
        const response = await updateRole({
          ...savedRole?.data,
          ...data,
          reportTo,
        });

        if (!isMutationSuccess(response)) {
          notifyMutationFailure(response, {
            title: `Could not update the ${data.name} role`,
            description:
              'Your changes were not saved. Please review the permissions and try again.',
          });
          return;
        }

        displayNotification({
          title: 'Successfully Updated',
          description: `Your have updated ${data.name} role`,
        });
      } else {
        const response = await createRole({ ...data, reportTo });

        if (!isMutationSuccess(response)) {
          notifyMutationFailure(response, {
            title: 'Could not create this role',
            description:
              'The role was not created. Please review the form and try again.',
          });
          return;
        }

        displayNotification({
          title: 'you have successfully added this role!',
          description: `Your have added ${data.name} role`,
        });
      }
      navigateToPreviousPage();
    } catch (error) {
      console.error(error);
      displayNotification({
        title: 'Something went wrong!',
        description:
          'Your changes were not saved. Please try this action later or contact BrickLynk support.',
        status: 'error',
      });
    }
  }

  useEffect(() => {
    if (savedRole?.data) {
      const { name, description, permission, reportTo, appRoleStatus } =
        savedRole.data;

      reset({
        reportTo: reportTo?.id,
        name,
        description,
        permission: permission.map(permission => ({
          ...permission,
          permissionActions: permission.permissionActions.map(action => ({
            ...action,
            enabled: action.enabled ? 'true' : 'false',
          })),
        })),
        appRoleStatus,
      });
    }
  }, [reset, savedRole?.data]);

  if (isAllActionsLoading) return <CenteredSpinner />;

  return (
    <Box>
      <HStack justify="space-between" mt="4" align="start">
        <Heading color="custom_blue.1" fontSize="2xl">
          {roleId ? savedRole?.data.name : 'Create New Role'}
        </Heading>
      </HStack>

      <VStack
        as="form"
        align="stretch"
        gap="5"
        onSubmit={handleSubmit(onSubmit)}
        bg="white"
        border="1px"
        borderColor="custom_gray.3"
        mt="7"
        mb="4"
        p="5"
        borderRadius="md"
        minH="80vh"
        h="auto"
      >
        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Role Name
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.name} flexBasis="50%">
            <Input
              placeholder="Role Name"
              {...register('name', {
                required: 'Role name is required',
              })}
            />
            <FormErrorMessage>
              {errors.name && errors.name.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Description
            </Box>
          </VStack>
          <FormControl flexBasis="50%">
            <Textarea placeholder="Description" {...register('description')} />
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Role Status
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.appRoleStatus} flexBasis="70%">
            <Controller
              control={control}
              name="appRoleStatus"
              rules={{ required: 'Role status is required' }}
              render={({ field }) => (
                <FormControl>
                  <RadioGroup {...field}>
                    {[
                      {
                        value: CompanyStatus.ACTIVE,
                        text: 'Active',
                      },
                      {
                        value: CompanyStatus.INACTIVE,
                        text: 'Inactive',
                      },
                    ].map(({ value, text }) => (
                      <Radio
                        key={value}
                        value={value}
                        mr="4"
                        colorScheme="orange"
                      >
                        {text}
                      </Radio>
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            />
            <FormErrorMessage>
              {errors.appRoleStatus && errors.appRoleStatus.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Report to
            </Box>
          </VStack>
          <FormControl flexBasis="50%" mt="2">
            <Select {...register('reportTo')}>
              <option />
              {allRoles?.data.content
                .filter(({ id }) => id !== Number(roleId))
                .map(({ id, name }) => (
                  <option key={id} value={String(id)}>
                    {name}
                  </option>
                ))}
            </Select>
          </FormControl>
        </HStack>
        <Divider />

        <Heading
          color="custom_blue.1"
          fontWeight="bold"
          fontSize="md"
          mt="8"
          mb="2"
        >
          Permissions
        </Heading>

        <PermissionList control={control} setValue={setValue} watch={watch} />

        <HStack mt={4} justify="end">
          <Button onClick={navigateToPreviousPage}>Cancel</Button>
          <Button
            isLoading={isSubmitting}
            type="submit"
            bg="custom_orange.2"
            _hover={{ bg: 'custom_orange.2' }}
            color="white"
          >
            Save
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};
